"use client";

import { OffersOffer } from '@/services/dataTypes';
import React, { useEffect, useState } from 'react';
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks';
import Banner from './Banner';
import { apiCategoryOfferBanners } from '@/apis/user';

interface Props {
    bannerResponse: OffersOffer[];
    domain: string;
    mer_slug: string;
    slug_type: string;
    categoryId: string;
    companyId: string;
}

const VerticalCategoryOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type, categoryId, companyId }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([]);
    const [page, setPage] = useState(2);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        const initialFiltered = filterOfferBanners(bannerResponse, 50, 2000, 65, 2000);
        setBanners(initialFiltered);
    }, [bannerResponse]);

    const loadMore = async () => {
        if (loading || !hasMore) return;
        setLoading(true);
        try {
            const res = await apiCategoryOfferBanners(categoryId, companyId, page);
            const newBanners = filterOfferBanners(res.data?.offers || [], 50, 2000, 65, 2000);

            setBanners(prev => [...prev, ...newBanners]);
            if (res?.data?.pagination?.next_page) {
                setPage(prev => prev + 1);
                setIsExpanded(true);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error loading more vertical banners", error);
        } finally {
            setLoading(false);
        }
    };

    const showLess = () => {
        const initialFiltered = filterOfferBanners(bannerResponse, 50, 2000, 65, 2000);
        setBanners(initialFiltered);
        setPage(2);
        setHasMore(true);
        setIsExpanded(false);
    };

    if (banners?.length === 0) return null;

    return (
        <div className="relative group/sidebar w-full">
            <div className="relative bg-white rounded-xl p-5 shadow-[0_4px_20px_rgba(30,58,95,0.06)] border border-[#E5E7EB] overflow-hidden transition-all duration-300 hover:shadow-[0_8px_30px_rgba(30,58,95,0.1)]">
                
                {/* Subtle Decorative Element */}
                <div className="absolute top-0 right-0 w-16 h-16 bg-[#F97316]/5 rounded-full blur-2xl -mr-8 -mt-8" />

                {/* Header */}
                <div className="relative mb-5 pb-4 border-b border-[#E5E7EB]">
                    <h4 className="text-base font-bold text-[#111827] flex items-center gap-2 font-sans">
                        <svg className="w-4 h-4 text-[#F97316]" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                        </svg>
                        Featured Offers
                    </h4>
                </div>

                {/* Banners List */}
                <div className="flex flex-col gap-3 mb-5">
                    {banners?.map((offer_data, i) => {
                        const dimension = getBannerDimensions(offer_data);
                        return (
                            <div key={i} className="rounded-lg overflow-hidden border border-[#E5E7EB] hover:border-[#F97316] transition-colors duration-200">
                                <Banner
                                    data={offer_data}
                                    height={dimension?.height}
                                    mer_slug={mer_slug}
                                    slug_type={slug_type}
                                    domain={domain}
                                    width={dimension?.width}
                                />
                            </div>
                        );
                    })}
                </div>

                {/* Action Buttons */}
                {(hasMore || isExpanded) && (
                    <div className={`flex w-full flex-col gap-2 ${isExpanded && hasMore ? '' : 'justify-center'}`}>
                        {hasMore && (
                            <button 
                                onClick={loadMore} 
                                disabled={loading}
                                className="w-full group/btn overflow-hidden rounded-lg py-2.5 px-4 bg-[#F97316] text-white hover:bg-[#EA580C] transition-all duration-300 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-sm hover:shadow-md font-sans"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className="text-sm font-semibold">Loading...</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="text-sm font-semibold">Show More</span>
                                        <svg className="w-4 h-4 group-hover/btn:translate-y-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        )}
                        {isExpanded && (
                            <button 
                                onClick={showLess} 
                                disabled={loading}
                                className="w-full group/btn-less overflow-hidden rounded-lg py-2.5 px-4 bg-white border border-[#E5E7EB] text-[#4B5563] hover:text-[#111827] hover:border-[#1E3A5F] hover:bg-[#F8FAFC] transition-all duration-300 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed font-sans"
                            >
                                <span className="text-sm font-semibold">Show Less</span>
                                <svg className="w-4 h-4 group-hover/btn-less:-translate-y-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerticalCategoryOfferBanner;
